/**
 * Visitor Home - For users without a profile
 * 
 * Primary actions:
 * - Join a session (default)
 * - Join a church (search)
 * - Create a church (small link)
 * 
 * Styled to match the Exbabel authentication design system (/signin):
 * - Page background: #F8FAFC with ambient indigo & blue blur flares
 * - Containers: rounded-[2rem] cards with shadow-[0_8px_30px_rgb(0,0,0,0.04)] and border-gray-100
 * - Buttons: #4F46E5, rounded-xl, font-semibold, shadow-indigo-200/50
 * - Typography: tracking-tight text-gray-900 titles, text-gray-500 descriptions
 */

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { JoinSessionModal } from '@/components/JoinSessionModal';

export function VisitorHome({ onJoinSession, onJoinChurch, onCreateChurch, onSignIn, onSignUp, onSignOut }) {
    const { user, isAuthenticated } = useAuth();
    const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);

    const handleJoin = (code) => {
        if (code && code.trim()) {
            onJoinSession(code.toUpperCase());
            setIsJoinModalOpen(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-[#F8FAFC] relative overflow-hidden">
            {/* Background Flare matching /signin */}
            <div className="absolute top-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-48 -left-48 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-[128px] opacity-70"></div>
                <div className="absolute top-48 -right-48 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-[128px] opacity-70"></div>
            </div>

            <Header onSignIn={onSignIn} onSignUp={onSignUp} onSignOut={onSignOut} />

            <div className="container mx-auto px-4 py-10 md:py-16 relative z-10 flex-1 flex flex-col justify-center">
                <div className="max-w-2xl mx-auto w-full">
                    {/* Hero */}
                    <div className="text-center mb-10">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-3">
                            Real-Time Translation
                        </h1>
                        <p className="text-base sm:text-lg text-gray-500 max-w-lg mx-auto">
                            Join a live session and hear translations in your language instantly
                        </p>
                    </div>

                    {/* Primary: Join Session Card matching /signin rounded-[2rem] card */}
                    <div className="w-full bg-white rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:border sm:border-gray-100 p-7 sm:p-10 mb-6 text-center">
                        <div className="text-5xl mb-3">📱</div>
                        <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-1.5">
                            Join a Session
                        </h2>
                        <p className="text-sm text-gray-500 mb-6">
                            Scan a QR code or enter a session code to join
                        </p>
                        <Button
                            className="w-full h-13 sm:h-14 text-base sm:text-lg rounded-xl bg-[#4F46E5] text-white hover:bg-indigo-700 transition-colors font-semibold shadow-md shadow-indigo-200/50 cursor-pointer"
                            onClick={() => setIsJoinModalOpen(true)}
                        >
                            Tap to Join Session
                        </Button>
                    </div>

                    {/* Secondary Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                        <div 
                            className="bg-white rounded-[1.75rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:border sm:border-gray-100 p-6 hover:border-indigo-200 hover:shadow-[0_12px_35px_rgb(79,70,229,0.06)] transition-all cursor-pointer text-center group" 
                            onClick={onJoinChurch}
                        >
                            <div className="text-3xl mb-2">⛪</div>
                            <h3 className="text-lg font-bold tracking-tight text-gray-900 group-hover:text-[#4F46E5] transition-colors mb-1">
                                Join a Church
                            </h3>
                            <p className="text-sm text-gray-500">
                                Become a member for regular access
                            </p>
                        </div>

                        <div 
                            className="bg-white rounded-[1.75rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:border sm:border-gray-100 p-6 hover:border-indigo-200 hover:shadow-[0_12px_35px_rgb(79,70,229,0.06)] transition-all cursor-pointer text-center group" 
                            onClick={() => {
                                if (onCreateChurch) {
                                    onCreateChurch();
                                } else {
                                    window.location.href = '/translate/checkout';
                                }
                            }}
                        >
                            <div className="text-3xl mb-2">✨</div>
                            <h3 className="text-lg font-bold tracking-tight text-gray-900 group-hover:text-[#4F46E5] transition-colors mb-1">
                                Start a Ministry
                            </h3>
                            <p className="text-sm text-gray-500">
                                Start a free trial to host live translations
                            </p>
                        </div>
                    </div>

                    {/* Signed in but no profile */}
                    {isAuthenticated && (
                        <p className="text-center text-gray-500 text-sm mt-8 font-medium">
                            Signed in as {user?.email}. Join a session or church to get started!
                        </p>
                    )}
                </div>
            </div>

            <JoinSessionModal
                isOpen={isJoinModalOpen}
                onClose={() => setIsJoinModalOpen(false)}
                onJoin={handleJoin}
            />
        </div>
    );
}
